import { useEffect, useState } from "react";
import {
    Button, Card, ConfigProvider, Empty, Modal, Popconfirm, Space, Spin, Statistic,
    Table, Tag, Typography, Row, Col,
} from "antd";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { ReloadOutlined, CalculatorOutlined, EyeOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";
import { displayNumbers } from "../../variables/numbersFunctions";

const PRIMARY = "#242166";
const fcfa = (v) => `${displayNumbers(Math.trunc(Number(v) || 0))} F`;

/**
 * Sous-menu Commissions → « Recalculer les commissions ».
 *
 * Liste les AGENCES (ou courtiers) qui possèdent un taux de commission PAR CATÉGORIE
 * (agrément validé) mais dont des contrats ont enregistré une commission NULLE — cas
 * typique d'un taux saisi/validé APRÈS la souscription. Le recalcul se lance
 * AGENCE PAR AGENCE (bouton par ligne), pour rester maître de l'opération et pouvoir
 * vérifier au fur et à mesure.
 */
const RecalculCommissions = () => {
    const [agences, setAgences] = useState([]);
    const [loading, setLoading] = useState(false);
    const [recalculEnCours, setRecalculEnCours] = useState({}); // { [codeAgence]: bool }
    // Modale de détail (aperçu des primes concernées avant recalcul).
    const [detail, setDetail] = useState({ open: false, agence: null, rows: [], loading: false });
    // Primes cochées dans la modale : permet un recalcul CIBLÉ plutôt que toute l'agence.
    const [selection, setSelection] = useState([]);

    const charger = () => {
        setLoading(true);
        axiosInstance
            .get("/api/contrat/commissions/recalcul/agences")
            .then((r) => setAgences(Array.isArray(r.data) ? r.data : []))
            .catch((e) => { console.log(e); toast.error("Erreur lors du chargement des agences."); })
            .finally(() => setLoading(false));
    };

    useEffect(() => { charger(); }, []);

    const recalculer = (codeAgence) => {
        setRecalculEnCours((p) => ({ ...p, [codeAgence]: true }));
        axiosInstance
            .post(`/api/contrat/commissions/recalcul/agence/${encodeURIComponent(codeAgence)}`)
            .then((r) => {
                const res = r.data || {};
                toast.success(
                    `${res.nbContratsRecalcules || 0} contrat(s) · ${res.nbReglementsMisAJour || 0} règlement(s) · `
                    + `${fcfa(res.commissionTotale)} recalculé(s).`,
                );
                // La ligne disparaît du reste à recalculer → on recharge la liste.
                charger();
            })
            .catch((e) => { console.log(e); toast.error("Erreur lors du recalcul."); })
            .finally(() => setRecalculEnCours((p) => ({ ...p, [codeAgence]: false })));
    };

    const ouvrirDetail = (agence) => {
        setSelection([]);
        setDetail({ open: true, agence, rows: [], loading: true });
        axiosInstance
            .get(`/api/contrat/commissions/recalcul/agence/${encodeURIComponent(agence.codeAgence)}/details`)
            .then((r) => setDetail((d) => ({ ...d, rows: Array.isArray(r.data) ? r.data : [], loading: false })))
            .catch((e) => { console.log(e); toast.error("Erreur lors du chargement du détail."); setDetail((d) => ({ ...d, loading: false })); });
    };

    // Recalcul CIBLÉ des primes cochées (endpoint dédié ; même scope/résolution de taux).
    const recalculerSelection = () => {
        const codeAgence = detail.agence?.codeAgence;
        if (!codeAgence || !selection.length) return;
        setRecalculEnCours((p) => ({ ...p, [codeAgence]: true }));
        axiosInstance
            .post(`/api/contrat/commissions/recalcul/agence/${encodeURIComponent(codeAgence)}/selection`,
                selection, { headers: { "Content-Type": "application/json" } })
            .then((r) => {
                const res = r.data || {};
                toast.success(`${res.nbContratsRecalcules || 0} prime(s) · ${fcfa(res.commissionTotale)} recalculé(s).`);
                setDetail({ open: false, agence: null, rows: [], loading: false });
                setSelection([]);
                charger();
            })
            .catch((e) => { console.log(e); toast.error("Erreur lors du recalcul."); })
            .finally(() => setRecalculEnCours((p) => ({ ...p, [codeAgence]: false })));
    };

    const totaux = agences.reduce((acc, a) => ({
        nbAgences: acc.nbAgences + 1,
        nbContrats: acc.nbContrats + (a.nbContratsAffectes || 0),
        nbReglements: acc.nbReglements + (a.nbReglementsAffectes || 0),
        commission: acc.commission + (a.commissionPotentielle || 0),
    }), { nbAgences: 0, nbContrats: 0, nbReglements: 0, commission: 0 });

    const columns = [
        {
            title: "Agence / Courtier",
            dataIndex: "nomAgence",
            key: "nomAgence",
            render: (v, r) => (
                <Space direction="vertical" size={0}>
                    <span style={{ fontWeight: 600, color: PRIMARY }}>{v || "—"}</span>
                    <Tag color="blue">{r.codeAgence}</Tag>
                </Space>
            ),
        },
        {
            title: "Mouvements",
            dataIndex: "nbContratsAffectes",
            key: "nb",
            align: "center",
            sorter: (a, b) => a.nbContratsAffectes - b.nbContratsAffectes,
            render: (v) => <Tag color="orange">{v}</Tag>,
        },
        {
            title: "Règlements",
            dataIndex: "nbReglementsAffectes",
            key: "nbr",
            align: "center",
            render: (v) => <Tag color="gold">{v}</Tag>,
        },
        {
            title: "Prime nette totale",
            dataIndex: "primeNetteTotale",
            key: "pn",
            align: "right",
            render: (v) => fcfa(v),
        },
        {
            title: "Commission à recalculer",
            dataIndex: "commissionPotentielle",
            key: "com",
            align: "right",
            sorter: (a, b) => a.commissionPotentielle - b.commissionPotentielle,
            render: (v) => <strong style={{ color: "#16a34a" }}>{fcfa(v)}</strong>,
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            render: (_, r) => (
                <Space>
                    <Button icon={<EyeOutlined />} onClick={() => ouvrirDetail(r)}>
                        Détails
                    </Button>
                    <Popconfirm
                        title="Recalculer les commissions ?"
                        description={`${r.nbContratsAffectes} mouvement(s) / ${r.nbReglementsAffectes} règlement(s) de ${r.nomAgence} seront mis à jour. Action historisée.`}
                        okText="Recalculer"
                        cancelText="Annuler"
                        onConfirm={() => recalculer(r.codeAgence)}
                    >
                        <Button type="primary" icon={<CalculatorOutlined />}
                            loading={!!recalculEnCours[r.codeAgence]}
                            style={{ background: PRIMARY, borderColor: PRIMARY }}>
                            Recalculer
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // Colonnes du détail (aperçu avant recalcul) : taux, valeur actuelle, valeur prévisionnelle.
    const detailColumns = [
        { title: "N° Police", dataIndex: "numeroPolice", key: "np", render: (v) => v || "—" },
        { title: "Souscripteur", dataIndex: "souscripteur", key: "s", render: (v) => v || "—" },
        { title: "Cat.", dataIndex: "categorie", key: "c", align: "center", render: (v) => v || "—" },
        { title: "Av.", dataIndex: "avenant", key: "av", align: "center", render: (v) => v || "—" },
        { title: "Date effet", dataIndex: "dateEffet", key: "d", render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—") },
        { title: "Prime nette", dataIndex: "primeNette", key: "pn", align: "right", render: (v) => fcfa(v) },
        { title: "Règl.", dataIndex: "nbReglements", key: "nr", align: "center" },
        { title: "Taux", dataIndex: "taux", key: "t", align: "center", render: (v) => <Tag color="blue">{v} %</Tag> },
        {
            title: "Commission actuelle", dataIndex: "commissionActuelle", key: "ca", align: "right",
            render: (v) => <span style={{ color: "#dc2626" }}>{fcfa(v)}</span>,
        },
        {
            title: "Commission prévisionnelle", dataIndex: "commissionPrevisionnelle", key: "cp", align: "right",
            render: (v) => <strong style={{ color: "#16a34a" }}>{fcfa(v)}</strong>,
        },
    ];

    const detailTotaux = (detail.rows || []).reduce((acc, r) => ({
        pn: acc.pn + (r.primeNette || 0),
        actuelle: acc.actuelle + (r.commissionActuelle || 0),
        prev: acc.prev + (r.commissionPrevisionnelle || 0),
    }), { pn: 0, actuelle: 0, prev: 0 });

    return (
        <ConfigProvider locale={frFR} theme={{ token: { colorPrimary: PRIMARY } }}>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <SideBar currentMenu={"COMMISSION"} />
                    <div className="layout-page">
                        <NavBar />
                        <div className="content-wrapper">
                            <div className="container-xxl flex-grow-1 container-p-y">
                                <Card
                                    style={{ borderRadius: 12, marginBottom: 16 }}
                                    title={
                                        <div>
                                            <Typography.Title level={4} style={{ margin: 0, color: PRIMARY }}>
                                                Recalculer les commissions
                                            </Typography.Title>
                                            <Typography.Text type="secondary">
                                                Agences/courtiers/intermédiaires dont des affaires ont une commission nulle malgré un taux paramétré
                                            </Typography.Text>
                                        </div>
                                    }
                                    extra={
                                        <Button icon={<ReloadOutlined />} onClick={charger} loading={loading}>
                                            Rafraîchir
                                        </Button>
                                    }
                                >
                                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                                        <Col xs={24} md={8}>
                                            <Card size="small" style={{ borderRadius: 10 }}>
                                                <Statistic title="Agences concernées" value={totaux.nbAgences}
                                                    valueStyle={{ color: PRIMARY }} />
                                            </Card>
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Card size="small" style={{ borderRadius: 10 }}>
                                                <Statistic title="Mouvements affectés" value={totaux.nbContrats}
                                                    valueStyle={{ color: "#d97706" }} />
                                            </Card>
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Card size="small" style={{ borderRadius: 10 }}>
                                                <Statistic title="Commission à recalculer" value={fcfa(totaux.commission)}
                                                    valueStyle={{ color: "#16a34a" }} />
                                            </Card>
                                        </Col>
                                    </Row>

                                    <Spin spinning={loading}>
                                        <Table
                                            rowKey="codeAgence"
                                            columns={columns}
                                            dataSource={agences}
                                            pagination={{ showSizeChanger: true, defaultPageSize: 20 }}
                                            locale={{
                                                emptyText: <Empty description="Aucune commission nulle à recalculer" />,
                                            }}
                                        />
                                    </Spin>
                                </Card>
                            </div>
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>

            {/* Détail des primes concernées AVANT recalcul (aperçu). */}
            <Modal
                title={detail.agence
                    ? `Détail — ${detail.agence.nomAgence} (${detail.agence.codeAgence})`
                    : "Détail"}
                open={detail.open}
                onCancel={() => setDetail({ open: false, agence: null, rows: [], loading: false })}
                width={1100}
                footer={null}
            >
                <Spin spinning={detail.loading}>
                    <Table
                        rowKey={(r, i) => r.codeId || `${r.numeroPolice}-${i}`}
                        rowSelection={{
                            selectedRowKeys: selection,
                            onChange: (keys) => setSelection(keys),
                        }}
                        columns={detailColumns}
                        dataSource={detail.rows}
                        size="small"
                        pagination={{ showSizeChanger: true, defaultPageSize: 15 }}
                        locale={{ emptyText: <Empty description="Aucun mouvement" /> }}
                        summary={() => (
                            <Table.Summary fixed>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={6}><strong>TOTAL</strong></Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="right"><strong>{fcfa(detailTotaux.pn)}</strong></Table.Summary.Cell>
                                    <Table.Summary.Cell index={6} colSpan={2} />
                                    <Table.Summary.Cell index={8} align="right"><strong style={{ color: "#dc2626" }}>{fcfa(detailTotaux.actuelle)}</strong></Table.Summary.Cell>
                                    <Table.Summary.Cell index={9} align="right"><strong style={{ color: "#16a34a" }}>{fcfa(detailTotaux.prev)}</strong></Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                    {detail.agence && (
                        <div style={{ marginTop: 12, textAlign: "right" }}>
                            <Space>
                                {/* Recalcul CIBLÉ : uniquement les primes cochées. */}
                                <Popconfirm
                                    title="Recalculer la sélection ?"
                                    description={`${selection.length} prime(s) sélectionnée(s) seront mises à jour. Action historisée.`}
                                    okText="Recalculer" cancelText="Annuler"
                                    onConfirm={recalculerSelection}
                                    disabled={!selection.length}
                                >
                                    <Button type="primary" icon={<CalculatorOutlined />}
                                        disabled={!selection.length}
                                        loading={!!recalculEnCours[detail.agence.codeAgence]}
                                        style={selection.length ? { background: PRIMARY, borderColor: PRIMARY } : undefined}>
                                        Recalculer la sélection ({selection.length})
                                    </Button>
                                </Popconfirm>
                                {/* Repli : toute l'agence, si l'utilisateur ne veut pas cocher une à une. */}
                                <Popconfirm
                                    title="Recalculer toute l'agence ?"
                                    description={`${detail.agence.nbContratsAffectes} mouvement(s) de ${detail.agence.nomAgence} seront mis à jour. Action historisée.`}
                                    okText="Recalculer" cancelText="Annuler"
                                    onConfirm={() => { recalculer(detail.agence.codeAgence); setDetail({ open: false, agence: null, rows: [], loading: false }); }}
                                >
                                    <Button icon={<CalculatorOutlined />}>Toute l'agence</Button>
                                </Popconfirm>
                            </Space>
                        </div>
                    )}
                </Spin>
            </Modal>
        </ConfigProvider>
    );
};

export default RecalculCommissions;
